import { CreditCardType } from "credit-card-type/src/types";
import { ControllerProps } from "react-hook-form";

import { CreditCardNetwork } from "chameleon/lib/components/display-cards/display-cards";

import { cardNetwork } from "checkout-experience/utils/validators/card-network";
import { cardNumber } from "checkout-experience/utils/validators/card-number";
import { isFutureExpiration } from "checkout-experience/utils/validators/is-future-expiration";
import {
  isValidExpiration,
  isValidMonthExpiration,
  isValidMonthForCurrentYear,
  isValidYearExpiration,
  isValidYearForCurrentMonth,
} from "checkout-experience/utils/validators/is-valid-expiration";
import { isValidRoutingNumber } from "checkout-experience/utils/validators/is-valid-routing-number";
import { matchesField } from "checkout-experience/utils/validators/matches-field";
import { noInvalidHyphenOrSpace } from "checkout-experience/utils/validators/no-invalid-hyphen-or-space";
import {
  CA_POSTAL_SPACE_ALPHA_NUMERIC,
  NO_PAN_IN_FIELD,
  NUMERIC_DATE,
  NUMERIC_ONLY,
  ORB_POSTAL,
  SPACE_UPPER_LOWER_ALPHA_NUMERIC,
  US_POSTAL_NUMERIC,
} from "checkout-experience/utils/validators/patterns";

export const getValidatorsOrb = (
  formId?: string,
  allowedCardNetworks?: CreditCardNetwork[],
  cardType?: CreditCardType,
  expiryDateRequired: boolean = true
): Record<string, ControllerProps["rules"]> => ({
  billingTitle: {},
  doAddressesMatch: {},
  billingAddressName: {
    // This is actually dropped and never used for credit card... we probably should rethink this field.
    required: true,
    maxLength: 30,
    minLength: 4,
    pattern: NO_PAN_IN_FIELD,
    validate: {
      hasSpace: (value: string) => {
        // return true if value has a space
        return value.trim().includes(" ");
      },
    },
  },
  billingAddressCountry: {
    required: true,
  },
  billingAddressAddressLine1: {
    required: true,
    maxLength: 30,
    pattern: NO_PAN_IN_FIELD,
  },
  billingAddressAddressLine2: {
    maxLength: 30,
    pattern: NO_PAN_IN_FIELD,
  },
  billingAddressCity: {
    required: true,
    maxLength: 20,
    pattern: NO_PAN_IN_FIELD,
  },
  billingAddressAdministrativeArea: {
    required: true,
    maxLength: 100,
  },
  billingAddressAdministrativeAreaUS: {
    required: true,
    maxLength: 2,
    pattern: SPACE_UPPER_LOWER_ALPHA_NUMERIC,
  },
  billingAddressAdministrativeAreaCA: {
    required: true,
    maxLength: 2,
    pattern: SPACE_UPPER_LOWER_ALPHA_NUMERIC,
  },
  billingAddressAdministrativeAreaGB: {
    required: true,
    maxLength: 100,
    pattern: SPACE_UPPER_LOWER_ALPHA_NUMERIC,
  },
  billingAddressZip: {
    required: true,
    minLength: 3,
    maxLength: 10, // Postal codes are longer than this and ORB allows for up to 15 characters but this is still restricted.  Not sure why
    pattern: ORB_POSTAL,
    validate: {
      isValidZip: noInvalidHyphenOrSpace(),
    },
  },
  billingAddressZipUS: {
    required: true,
    minLength: 5,
    maxLength: 5,
    pattern: US_POSTAL_NUMERIC,
  },
  billingAddressZipCA: {
    required: true,
    minLength: 6,
    maxLength: 7,
    pattern: CA_POSTAL_SPACE_ALPHA_NUMERIC,
  },
  billingAddressZipGB: {
    required: true,
    minLength: 6,
    maxLength: 8,
    pattern: ORB_POSTAL,
    validate: {
      isValidZip: noInvalidHyphenOrSpace(),
    },
  },
  cardNumber: {
    required: true,
    minLength: cardType?.lengths.sort()[0] ?? 16,
    maxLength:
      (cardType?.lengths.sort().slice(-1)[0] ?? 16) +
      (cardType?.gaps.length ?? 3),
    // pattern: /"^[0-9 ]*$/,
    validate: {
      cardNetwork: cardNetwork({ allowedCardNetworks }),
      cardNumber: cardNumber(),
    },
  },
  expiration: {
    required: expiryDateRequired,
    minLength: expiryDateRequired ? 5 : 0,
    maxLength: 7,
    pattern: NUMERIC_DATE,
    validate: {
      isValidExpiration: isValidExpiration(),
      isFutureExpiration: isFutureExpiration(),
    },
  },
  expirationMonth: {
    required: expiryDateRequired,
    validate: {
      isValidMonthExpiration: isValidMonthExpiration(),
      isValidMonthForCurrentYear: isValidMonthForCurrentYear(
        "expirationYear",
        formId
      ),
    },
  },
  expirationYear: {
    required: expiryDateRequired,
    validate: {
      isValidYearExpiration: isValidYearExpiration(),
      isFutureExpiration: isValidYearForCurrentMonth("expirationMonth", formId),
    },
  },
  securityCode: {
    required: true,
    minLength: 3,
    maxLength: 4,
    pattern: NUMERIC_ONLY,
  },
  nameOnCard: {
    required: true,
    maxLength: 30, // this is mapped to avsName which is 30 chars and doesn't get sent with cardholder data
    minLength: 4,
    pattern: NO_PAN_IN_FIELD,
    validate: {
      hasSpace: (value: string) => {
        // return true if value has a space
        return value.trim().includes(" ");
      },
    },
  },
  accountType: {
    required: true,
  },
  nameOnAccount: {
    required: true,
    pattern: NO_PAN_IN_FIELD,
    validate: {
      hasSpace: (value: string) => {
        // return true if value has a space
        return value.trim().includes(" ");
      },
    },
  },
  routingNumber: {
    required: true,
    pattern: NUMERIC_ONLY,
    minLength: 9,
    maxLength: 9,
    validate: {
      isValidRoutingNumber: isValidRoutingNumber(),
    },
  },
  accountNumber: {
    required: true,
    pattern: NUMERIC_ONLY,
    maxLength: 17,
  },
  confirmAccountNumber: {
    required: true,
    maxLength: 17,
    pattern: NUMERIC_ONLY,
    validate: {
      matchesField: matchesField(
        "confirmAccountNumber",
        "accountNumber",
        formId
      ),
    },
  },
  confirmRoutingNumber: {
    required: true,
    pattern: NUMERIC_ONLY,
    minLength: 9,
    maxLength: 9,
    validate: {
      isValidRoutingNumber: isValidRoutingNumber(),
      matchesField: matchesField(
        "confirmRoutingNumber",
        "routingNumber",
        formId
      ),
    },
  },
  giftCardNumber: {
    required: true,
    minLength: 12,
    maxLength: 19,
    pattern: NUMERIC_ONLY,
  },
  pinCode: {
    required: false,
    pattern: NUMERIC_ONLY,
  },
});
